import {
    PlaneGeometry,
    Mesh,
    MeshPhongMaterial,
    Clock,
    Color,
    LinearToneMapping,
    PerspectiveCamera,
    Scene,
    Vector2,
    Vector3,
    Vector4,
    WebGLRenderer,
} from 'three'
import gsap from 'gsap'
import Lights from './Lights'
import Controls from './Controls'
import RenderPipeline from './post/RenderPipeline'
import QA from './Qa'
import Lines from './Lines'
import NodeHighlightAnimation from './NodeHighlightAnimation'
import Particles from './Particles'
import Autopilot from './Autopilot'
import getMapMaterials from './utils/getMapMaterials'
import getHits from './utils/getHits'
import getCoordinatesArea from './utils/getCoordinatesArea'
import injectRenderPipeline from './utils/injectRenderPipeline'
import postpone from './utils/postpone'
import getTopology from './utils/getTopology'

function now() {
    return (typeof performance === 'undefined' ? Date : performance).now()
}

// Points emerge from around central europe.
const ANCHOR_POINT = new Vector3(0.25, 0, -1.0)

const NORMAL_SCALE_X = -2.235 // 1.49 * 1.5

const NORMAL_SCALE_Y = -0.735 // 0.49 * 1.5

export const LATI_TRANS = 2 / 90

export const LONG_TRANS = 4 / 180

const MAX_CONNECTIONS = 32

const defaultOffsetFn = () => ({
    x: 0,
    y: 0,
})

export default function NetworkVisualizer(canvas, {
    offsetFn = defaultOffsetFn,
    setLoading,
    onIntroDone,
    onPan = () => {},
    onSelect = () => {},
}) {
    let nodeCoordinates

    let metadata

    let time

    let highlightedPoint

    let hoveredPoint

    let selectedPoint

    const state = {
        currentTime: 0,
        endDragDelay: null,
        isDragging: false,
        pauseTime: 0,
        pauseTimeStart: 0,
        previousTime: 0,
    }

    let { ratio = 1, width = 2, height = 2 } = {}

    const pixelRatio = width / height

    let renderer

    let anisotropy

    const scene = new Scene()

    scene.background = new Color(0xffffff)

    const sceneMirror = new Scene()

    const lights = new Lights()

    lights.mount(scene)

    const camera = new PerspectiveCamera(60, width / height, 0.1, 20)

    camera.lookAt(new Vector3())
    camera.position.set(0, 1, 1)

    let renderPipeline

    let highlightAnimation

    let staleShadows = false

    let introDone = false

    let staleShadowsTimeoutId

    let autopilot

    function engageAutopilot() {
        if (autopilot) {
            autopilot.engage({
                delay: 3500,
            })
        }
    }

    function disengageAutopilot() {
        if (autopilot) {
            autopilot.disengage()
        }
    }

    let selectPoint

    const invalidateShadows = () => {
        if (introDone) {
            staleShadows = true

            clearTimeout(staleShadowsTimeoutId)

            staleShadowsTimeoutId = setTimeout(() => {
                staleShadows = false
            }, 500)
        }
    }

    const planeGeometry = new PlaneGeometry(100, 100, 1)

    const planeMaterial = new MeshPhongMaterial({
        color: 0xEAEEF9,
    })

    planeMaterial.depthWrite = false

    const plane = new Mesh(planeGeometry, planeMaterial)

    plane.rotateX(-Math.PI * 0.5)
    plane.position.y = -0.02
    plane.castShadow = false
    plane.receiveShadow = false

    scene.add(plane)

    const particles = new Particles(camera, {
        onBeforeQualityChange: (particle) => {
            if (particle) {
                scene.remove(particle.instance)
                sceneMirror.remove(particle.instanceMirror)
            }
        },
        onAfterQualityChange: ({ instance, instanceMirror }) => {
            scene.add(instance)
            sceneMirror.add(instanceMirror)
        },
        onIntroDone: () => {
            staleShadows = false
            introDone = true

            autopilot = new Autopilot(
                particles.getCoordinates(),
                (point) => (
                    selectPoint(point, {
                        autopilot: true,
                        maxConnections: 12,
                    })
                ),
            )

            if (!selectedPoint) {
                engageAutopilot()
            }

            if (typeof onIntroDone === 'function') {
                onIntroDone()
            }
        },
    })

    let lines

    let connectedPoints

    let onPointSelect

    const clearSelection = () => {
        if (selectedPoint) {
            particles.deselect(selectedPoint, time)
            highlightAnimation.hide(selectedPoint)
            selectedPoint = undefined
        }

        if (connectedPoints) {
            connectedPoints.forEach((point) => {
                particles.deactive(point)
            })

            lines.setTopology(time, undefined)

            connectedPoints = undefined
        }

        if (typeof onSelect === 'function') {
            onSelect(null, {})
        }
    }

    let cancelPostponed

    const nodeCache = {}

    const streamCache = {}

    let fetchNo = 0

    selectPoint = async (point, { autopilot: autopiloted = false, maxConnections = MAX_CONNECTIONS } = {}) => {
        if (point === selectedPoint) {
            return
        }

        const currentFetchNo = ++fetchNo

        if (typeof cancelPostponed === 'function') {
            // Cancel all pending topology displaying. See below.
            cancelPostponed()
            cancelPostponed = undefined
        }

        clearSelection()

        if (typeof setLoading === 'function') {
            setLoading(false)
        }

        if (!point) {
            return
        }

        selectedPoint = point

        particles.select(point, time)

        highlightAnimation.show(point)

        invalidateShadows()

        if (typeof onPointSelect === 'function') {
            // "Preflight" animation. We don't know the topology's area so we don't touch it, just
            // center the selected point and that's it.
            onPointSelect(point, {
                area: undefined,
                duration: autopiloted ? 12 : 1,
                showTooltip: false,
            })
        }

        if (typeof setLoading === 'function') {
            setLoading(true)
        }

        const topology = await (async () => {
            try {
                return await getTopology(point, {
                    nodeCache,
                    streamCache,
                    metadata,
                    nodeCoordinates,
                })
            } catch (e) {
                console.warn('Failed to load topology', e)
                return []
            }
        })()

        if (currentFetchNo !== fetchNo) {
            // Skip the rest if another particle has been clicked in the meantime. The one clicked
            // most recently wins.
            return
        }

        if (typeof setLoading === 'function') {
            setLoading(false)
        }

        const partialTopology = [...new Set([
            ...topology.filter(([a, b]) => a === point || b === point),
            ...topology,
        ])].slice(0, maxConnections)

        const points = new Set([point])

        partialTopology.forEach(([a, b]) => {
            points.add(a)
            points.add(b)
        })

        const coordinates = [...points]

        if (typeof onPointSelect === 'function') {
            onPointSelect(point, {
                area: autopiloted ? 1.0 + Math.random() : getCoordinatesArea(coordinates) * (0.5 + Math.random() * 0.25),
                duration: autopiloted ? 12 : 1,
                showTooltip: !autopiloted,
            })
        }

        coordinates.forEach((c) => {
            if (c !== point) {
                particles.active(c)
            }
        })

        connectedPoints = coordinates

        const [cancel, postponed] = postpone(250, () => {
            // Let's wait for the current selection to go away.
            lines.setTopology(time, partialTopology)
        })

        // eslint-disable-next-line require-atomic-updates
        cancelPostponed = cancel

        await postponed()
    }

    let controls

    let qa

    let frameRequest

    let clock

    let lastRenderAt

    const loop = () => {
        if (lastRenderAt != null && now() - lastRenderAt < 1000 / 60) {
            frameRequest = requestAnimationFrame(loop)
            return
        }

        const delta = clock.getDelta()

        state.currentTime = clock.elapsedTime + state.previousTime

        time = state.currentTime - state.pauseTime

        qa.check(time, delta)

        controls.update(time, delta)

        onPan(camera)

        if (!state.isDragging) {
            renderPipeline.update(time)
            particles.update(time)
            lines.update(time)

            if (staleShadows) {
                lights.update()
            }
        }

        renderPipeline.render()

        lastRenderAt = now()

        frameRequest = requestAnimationFrame(loop)
    }

    this.resize = ({ width: w, height: h } = {}) => {
        if (w != null && h != null) {
            width = w
            height = h
            ratio = width / height
            camera.aspect = ratio

            camera.updateProjectionMatrix()
        }

        if (renderPipeline) {
            renderPipeline.setSize(width, height)
        }

        if (renderer) {
            renderer.setSize(width, height)
        }

        if (controls) {
            controls.resize()
        }
    }

    const setQuality = (quality) => {
        switch (quality) {
            case 0:
                particles.setQuality(0)
                lights.setCastShadow(false)
                break
            case 1:
                particles.setQuality(0)
                lights.setCastShadow(true)
                break
            default:
                particles.setQuality(1)
                lights.setCastShadow(true)
        }

        renderPipeline.setQuality(quality)

        renderPipeline.setSize(width, height)
    }

    const onBeforeLoad = () => {
        if (renderer) {
            throw new Error('Already loaded')
        }

        renderer = new WebGLRenderer({
            alpha: false,
            antialias: true,
            canvas,
            depth: true,
            logarithmicDepthBuffer: false,
            powerPreference: 'high-performance',
            preserveDrawingBuffer: false,
            stencil: false,
        })

        renderer.setClearColor(0x000000, 1)
        renderer.toneMapping = LinearToneMapping
        renderer.toneMappingExposure = 0.6
        renderer.shadowMap.enabled = true
        renderer.setSize(width, height)
        renderer.setPixelRatio(pixelRatio)

        anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy())

        highlightAnimation = new NodeHighlightAnimation()

        renderPipeline = new RenderPipeline(renderer, scene, sceneMirror, camera, {
            connectedColor: new Color(0xe0500d),
            fullBlur: 0.0,
            glowScale: 2,
            glowStrength: 0.8,
            offsetTilt: new Vector4(1.5, 1.0, 1.5, 1.0),
            radius: 2,
            shift: new Vector4(4.0, 1000.0, 4.0, 1000.0),
        })

        injectRenderPipeline(renderPipeline, particles.getParticleMaterial())

        lines = new Lines(scene, renderPipeline, MAX_CONNECTIONS)

        qa = new QA(0, 3, setQuality, 2.5)
    }

    this.load = async (onComplete) => {
        onBeforeLoad()

        const normalScale = new Vector2(0, 0)

        await highlightAnimation.load().then(scene.add.bind(scene))

        injectRenderPipeline(renderPipeline, highlightAnimation.getMaterial())

        const globeMaterials = await getMapMaterials(anisotropy, normalScale, {
            onBeforeLoad: (materials) => {
                materials.forEach((m) => {
                    injectRenderPipeline(renderPipeline, m)
                })
            },
            onModelsLoaded: (modelsObject) => {
                scene.add(modelsObject)

                if (typeof onComplete === 'function') {
                    onComplete()
                }
            },
        })

        gsap.to({}, {
            duration: 3,
            delay: 2,
            ease: 'linear',
            onUpdate() {
                const t = this.progress()

                // Materials share `normalScale`.
                Object.assign(normalScale, {
                    x: NORMAL_SCALE_X * t,
                    y: NORMAL_SCALE_Y * t,
                })

                globeMaterials.forEach((material) => {
                    // eslint-disable-next-line no-param-reassign
                    material.needsUpdate = true
                })
            },
        })
    }

    this.feed = ({ coordinates: _coordinates, metadata: _metadata, nodeCoordinates: _nodeCoordinates }) => {
        nodeCoordinates = _nodeCoordinates

        // We're gonna sort coordinates thus we copy it to store the original.
        const coordinates = [..._coordinates]
            .map((point) => [
                point,
                ANCHOR_POINT.distanceToSquared(point),
            ])
            .sort(([, distanceA], [, distanceB]) => distanceA - distanceB)
            .map(([point]) => point)

        const timing = new WeakMap()

        coordinates.forEach((point, i) => {
            timing.set(point, i * (0.005 + 0.015 * (i / coordinates.length)))
        })

        metadata = _metadata

        particles.setCoordinates(coordinates, timing)

        staleShadows = true
    }

    const getIntroedHit = (x, y) => {
        const point = getHits(x, y, width, height, particles.getCoordinates(), camera)[0]

        if (particles.isIntroDone(time, {
            point,
        })) {
            return point
        }

        return undefined
    }

    const onBeforeFirstStart = () => {
        this.resize()

        scene.background = new Color(0x0)

        clock = new Clock()

        controls = new Controls(canvas, {
            camera,
            lookAtPosition: new Vector3(0, 0, 0),
            onClick: async ({ target, x, y }) => {
                const hit = getIntroedHit(x, y)

                try {
                    await selectPoint(hit)
                } catch (e) {
                    console.warn('Failed to complete point selection', e)
                }

                if (!hit) {
                    target.resetZoom()
                    engageAutopilot()
                }
            },
            onDragEnd: () => {
                state.endDragDelay = setTimeout(() => {
                    state.isDragging = false
                    state.pauseTime = state.pauseTime + state.currentTime - state.pauseTimeStart
                }, 250)
            },
            onDragStart: ({ target }) => {
                clearTimeout(state.endDragDelay)

                state.isDragging = true
                state.pauseTimeStart = state.currentTime

                target.killMovement()
                target.killZoomMovement()
            },
            onHover: ({ x, y }) => {
                const hit = getIntroedHit(x, y)

                hoveredPoint = hit

                const isCurrent = !!hit && (hit === highlightedPoint || hit === selectedPoint)

                if (highlightedPoint && !isCurrent) {
                    if (highlightedPoint !== selectedPoint) {
                        invalidateShadows()
                        particles.dehighlight(highlightedPoint, time)
                    }

                    highlightedPoint = [][0] // Empty `hit`.
                }

                if (hit && !isCurrent) {
                    Object.assign(canvas.style, {
                        cursor: 'pointer',
                    })

                    invalidateShadows()

                    particles.highlight(hit, time)

                    highlightedPoint = hit
                }

                if (!hit) {
                    Object.assign(canvas.style, {
                        cursor: 'default',
                    })
                }

                if (hit) {
                    disengageAutopilot()
                    return
                }

                if (!hit && !selectedPoint) {
                    engageAutopilot()
                }
            },
            onPointerDown: ({ target }) => {
                if (hoveredPoint && hoveredPoint !== selectedPoint) {
                    Object.assign(canvas.style, {
                        cursor: 'pointer',
                    })

                    particles.down(hoveredPoint, time)
                }

                disengageAutopilot()

                target.killMovement()
                target.killZoomMovement()
            },
            zoomLevels: [0.9, 0.7, 1.0, 0.8],
        })

        onPointSelect = (point, { area, showTooltip = true, duration = 1.0 } = {}) => {
            controls.moveTo(point, area, duration, offsetFn(canvas.width))

            onSelect(point, {
                metadata: metadata.get(point),
                showTooltip,
            })
        }
    }

    const onAfterFirstStart = () => {
        lights.intro()
    }

    let visibilityCheck

    let playing = false

    this.start = () => {
        if (playing) {
            return
        }

        if (typeof document === 'undefined') {
            return
        }

        if (!visibilityCheck) {
            visibilityCheck = () => {
                document.hidden ? this.stop() : this.start()
            }

            // Pause if tab isn't visible
            document.addEventListener('visibilitychange', visibilityCheck, false)
        }

        if (document.hidden) {
            return
        }

        const isFirstStart = !controls

        if (isFirstStart) {
            onBeforeFirstStart()
        }

        playing = true
        clock.start()
        loop()

        if (isFirstStart) {
            onAfterFirstStart()
        }

        if (!selectedPoint) {
            engageAutopilot()
        }
    }

    this.stop = () => {
        if (!playing) {
            return
        }

        disengageAutopilot()
        state.previousTime = state.currentTime
        clock.stop()
        cancelAnimationFrame(frameRequest)
        frameRequest = undefined
        playing = false
    }
}
